import Phaser from 'phaser';
import { BaseScene } from '@core/scenes/BaseScene';
import { SceneKeys, GAME_TITLE, Palette } from '@core/config/constants';
import { MinigameRegistry } from '@core/registry/MinigameRegistry';
import type { MinigameDefinition } from '@core/types';

const CARD_WIDTH = 260;
const CARD_HEIGHT = 200;
const CARD_GAP = 40;
const CARDS_PER_ROW = 4;

/**
 * Menu principal del juego. Lee el catalogo de minijuegos del registry y dibuja una
 * tarjeta por cada uno. Tocar una tarjeta inicia la escena del minijuego.
 */
export class HubScene extends BaseScene {
  public constructor() {
    super(SceneKeys.Hub);
  }

  public create(): void {
    this.add.rectangle(this.centerX, this.centerY, this.scale.width, this.scale.height, Palette.background);

    this.add
      .text(this.centerX, 80, GAME_TITLE, {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '64px',
        color: Palette.textLight,
      })
      .setOrigin(0.5);

    this.renderCoins();
    this.renderMinigameCards(MinigameRegistry.list());
  }

  private renderCoins(): void {
    this.add
      .text(this.scale.width - 40, 40, `⭐ ${this.profile.coins}`, {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '32px',
        color: Palette.textLight,
      })
      .setOrigin(1, 0);
  }

  private renderMinigameCards(minigames: MinigameDefinition[]): void {
    if (minigames.length === 0) {
      this.add
        .text(this.centerX, this.centerY, 'Pronto vas a tener minijuegos aca!', {
          fontFamily: 'Trebuchet MS, sans-serif',
          fontSize: '28px',
          color: Palette.textLight,
        })
        .setOrigin(0.5);
      return;
    }

    const rowCount = Math.ceil(minigames.length / CARDS_PER_ROW);
    const gridHeight = rowCount * CARD_HEIGHT + (rowCount - 1) * CARD_GAP;
    const startY = this.centerY - gridHeight / 2 + CARD_HEIGHT / 2 + 40;

    minigames.forEach((minigame, index) => {
      const column = index % CARDS_PER_ROW;
      const row = Math.floor(index / CARDS_PER_ROW);
      const cardsInRow = Math.min(CARDS_PER_ROW, minigames.length - row * CARDS_PER_ROW);
      const rowWidth = cardsInRow * CARD_WIDTH + (cardsInRow - 1) * CARD_GAP;
      const startX = this.centerX - rowWidth / 2 + CARD_WIDTH / 2;

      const x = startX + column * (CARD_WIDTH + CARD_GAP);
      const y = startY + row * (CARD_HEIGHT + CARD_GAP);
      this.createCard(minigame, x, y);
    });
  }

  private createCard(minigame: MinigameDefinition, x: number, y: number): void {
    const container = this.add.container(x, y);

    const background = this.add.graphics();
    background.fillStyle(minigame.color, 1);
    background.fillRoundedRect(-CARD_WIDTH / 2, -CARD_HEIGHT / 2, CARD_WIDTH, CARD_HEIGHT, 22);

    const icon = this.add
      .text(0, -40, minigame.icon, { fontSize: '64px' })
      .setOrigin(0.5);

    const title = this.add
      .text(0, 50, minigame.title, {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '26px',
        color: Palette.textLight,
        align: 'center',
        wordWrap: { width: CARD_WIDTH - 30 },
      })
      .setOrigin(0.5);

    container.add([background, icon, title]);
    container.setSize(CARD_WIDTH, CARD_HEIGHT);
    container.setInteractive({ useHandCursor: true });

    container.on(Phaser.Input.Events.POINTER_OVER, () => container.setScale(1.05));
    container.on(Phaser.Input.Events.POINTER_OUT, () => container.setScale(1));
    container.on(Phaser.Input.Events.POINTER_UP, () => {
      this.audio.playClick();
      this.scene.start(minigame.key);
    });
  }
}
